import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRAND, NAV } from "@/data/content";
import logo from "@/assets/tanseeq-logo.png";

export const Header = () => {
  const { t, lang, toggle, isAr } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <>
      <div className="hidden md:block bg-navy-deep text-white/70 text-xs" data-testid="top-utility-bar">
        <div className="container-tnsq flex items-center justify-between py-2.5">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" />
            {t(BRAND.agency)} · {isAr ? "رقم الهيئة" : "FTA No."} {BRAND.ftaNo}
          </span>
          <span className="flex items-center gap-4">
            <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-white transition-colors" dir="ltr">{BRAND.phone}</a>
            <span className="text-white/20">|</span>
            <a href={`mailto:${BRAND.email}`} className="hover:text-white transition-colors">{BRAND.email}</a>
          </span>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
          scrolled ? "bg-ivory/90 backdrop-blur-xl border-line shadow-soft" : "bg-ivory/70 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="container-tnsq flex items-center justify-between h-[76px] gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo-link">
            <img src={logo} alt={t(BRAND.name)} className="h-12 md:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) =>
              item.external ? (
                <a
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${item.key}`}
                  className="px-3 py-2 text-[0.9rem] font-medium whitespace-nowrap text-navy hover:text-brandgreen rounded-md transition-colors relative group inline-flex items-center gap-1.5"
                >
                  {t(item.label)}
                  <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                  <span className="absolute inset-x-4 -bottom-0.5 h-px bg-gold scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300" />
                </a>
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  data-testid={`nav-${item.key}`}
                  className="px-3 py-2 text-[0.9rem] font-medium whitespace-nowrap text-ink/70 hover:text-navy rounded-md transition-colors relative group"
                >
                  {t(item.label)}
                  <span className="absolute inset-x-4 -bottom-0.5 h-px bg-gold scale-x-0 group-hover:scale-x-100 origin-center transition-transform duration-300" />
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="h-9 min-w-[48px] px-3 rounded-full border border-navy/25 text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors"
              aria-label="Toggle language"
            >
              {lang === "en" ? "عربي" : "EN"}
            </button>
            <Link
              to="/#contact"
              data-testid="header-cta"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-navy-600 transition-colors shadow-soft"
            >
              <Phone className="h-3.5 w-3.5" />
              {t({ en: "Request consultation", ar: "اطلب استشارة" })}
            </Link>
            <button
              className="lg:hidden h-10 w-10 grid place-items-center rounded-md text-navy"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-line bg-ivory" data-testid="mobile-menu">
            <div className="container-tnsq py-4 flex flex-col gap-1">
              {NAV.map((item) =>
                item.external ? (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-${item.key}`}
                    className="px-2 py-3 text-base font-medium text-navy border-b border-line last:border-0 inline-flex items-center gap-2"
                  >
                    {t(item.label)} <ExternalLink className="h-4 w-4 opacity-60" />
                  </a>
                ) : (
                  <Link
                    key={item.key}
                    to={item.to}
                    data-testid={`mobile-nav-${item.key}`}
                    className="px-2 py-3 text-base font-medium text-ink/80 border-b border-line last:border-0"
                  >
                    {t(item.label)}
                  </Link>
                )
              )}
              <Link to="/#contact" className="mt-3 inline-flex items-center justify-center h-11 rounded-full bg-navy text-white font-semibold" data-testid="mobile-cta">
                {t({ en: "Request consultation", ar: "اطلب استشارة" })}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
